import { ExclamationTriangleIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

function SourceBadge({ source }) {
    const normalized = String(source || "").toLowerCase();

    if (normalized === "manual") {
        return (
            <span
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                style={{
                    background: "oklch(0.97 0.06 75)",
                    color: "oklch(0.42 0.16 75)",
                    border: "1px solid oklch(0.88 0.10 75)",
                }}
            >
                Manual
            </span>
        );
    }

    if (normalized.includes("fba")) {
        return (
            <span
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                style={{ background: "#E8F0FE", color: "oklch(0.30 0.14 220)" }}
            >
                Amazon FBA
            </span>
        );
    }

    if (normalized.includes("fbm") || normalized.includes("amazon")) {
        return (
            <span
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                style={{ background: "oklch(0.94 0.06 280 / 0.3)", color: "oklch(0.35 0.12 280)" }}
            >
                Amazon FBM
            </span>
        );
    }

    return <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{source || "-"}</span>;
}

function ChannelBadge({ channel }) {
    const normalized = String(channel || "").toLowerCase();

    if (normalized === "shopify") {
        return (
            <span
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                style={{ background: "oklch(0.94 0.06 145 / 0.3)", color: "oklch(0.30 0.14 145)" }}
            >
                Shopify
            </span>
        );
    }

    return (
        <span
            className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
            style={{ background: "#E8F0FE", color: "oklch(0.30 0.14 220)" }}
        >
            Amazon
        </span>
    );
}

export default function FinalMappingTable({ rows, totalRows, loading }) {
    const duplicateCount = rows.filter((row) => row.duplicate_flag).length;
    const thClass = "px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-200 whitespace-nowrap";

    if (loading && rows.length === 0) {
        return (
            <div className="flex items-center justify-center px-4 py-12 text-sm text-gray-500">
                Loading final mappings...
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col">
            {duplicateCount > 0 ? (
                <div
                    className="flex items-center gap-2 border-x border-b border-gray-200 px-4 py-2"
                    style={{ background: "oklch(0.98 0.04 25)", borderColor: "oklch(0.90 0.08 25)" }}
                >
                    <ExclamationTriangleIcon
                        className="h-4 w-4 shrink-0"
                        style={{ color: "oklch(0.60 0.22 25)" }}
                    />
                    <p className="text-xs font-medium" style={{ color: "oklch(0.45 0.18 25)" }}>
                        {duplicateCount.toLocaleString()} duplicate execution SKU{duplicateCount !== 1 ? "s" : ""} detected
                        {" "}highlighted in red. Resolve duplicates in the Manual SKU Mapping tab.
                    </p>
                </div>
            ) : null}

            <div className="flex-1 overflow-auto border-x border-b border-gray-200 bg-white">
                <table className="min-w-[960px] w-full border-collapse text-sm">
                    <thead className="sticky top-0 z-10 bg-white">
                        <tr>
                            <th className={thClass}>Execution SKU</th>
                            <th className={thClass}>Product</th>
                            <th className={`${thClass} w-24`}>Channel</th>
                            <th className={thClass}>Supply SKU</th>
                            <th className={`${thClass} w-32`}>Source</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-3 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center text-center">
                                        <MagnifyingGlassIcon className="mb-3 h-8 w-8 text-gray-300" aria-hidden />
                                        <p className="text-sm font-medium text-gray-500">No rows match your filters</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            rows.map((row, index) => (
                                <tr
                                    key={row.id || `${row.execution_sku}-${index}`}
                                    className={
                                        row.duplicate_flag
                                            ? "border-b border-gray-100 bg-red-50 transition-colors hover:bg-red-100/80"
                                            : `border-b border-gray-100 transition-colors hover:bg-gray-100/80 ${
                                                  index % 2 === 0 ? "bg-white" : "bg-gray-50/60"
                                              }`
                                    }
                                >
                                    <td className="px-3 py-2 text-xs">
                                        <div className="flex items-center gap-1.5">
                                            {row.duplicate_flag ? (
                                                <ExclamationTriangleIcon
                                                    className="h-3.5 w-3.5 shrink-0"
                                                    style={{ color: "oklch(0.60 0.22 25)" }}
                                                />
                                            ) : null}
                                            <span className={`font-medium ${row.duplicate_flag ? "text-red-700" : "text-gray-900"}`}>
                                                {row.execution_sku}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="max-w-[240px] px-3 py-2 text-xs">
                                        <span className="block truncate text-gray-500">{row.canonical_product_name || "-"}</span>
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        <ChannelBadge channel={row.channel} />
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        {row.supply_sku ? (
                                            <span className="inline-flex items-center rounded border border-gray-200 bg-white px-[10px] py-0.5 text-xs font-medium text-gray-900">
                                                {row.supply_sku}
                                            </span>
                                        ) : (
                                            <span className="italic text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        <SourceBadge source={row.source} />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="border border-t-0 border-gray-200 bg-gray-50 px-4 py-2">
                <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                        {rows.length.toLocaleString()} of {totalRows.toLocaleString()} rows · SKU Mapping Final · Read-only
                    </p>
                    {duplicateCount > 0 ? (
                        <span className="text-xs font-medium" style={{ color: "oklch(0.60 0.22 25)" }}>
                            {duplicateCount} duplicates
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
